import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Heart, MessageCircle, Bookmark, Repeat, X } from 'lucide-react';
import { postService } from '@/services/api';
import { UserAvatar } from '@/components/ui/Avatar';
import { formatRelativeDate, cn } from '@/lib/utils';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';
import QuotedContent from '@/components/feed/QuotedContent';
import ReplyCard from '@/components/feed/ReplyCard';
import QuoteModal, { type QuoteTarget } from '@/components/feed/QuoteModal';
import { useLongPress } from '@/hooks/useLongPress';

export default function PostPage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const { user } = useAuth();

  const [post, setPost] = useState<any>(null);
  const [replies, setReplies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const [replyContent, setReplyContent] = useState('');
  const [replyingToId, setReplyingToId] = useState<string | null>(null);

  const [isLiked, setIsLiked] = useState<boolean>(false);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [isReposted, setIsReposted] = useState<boolean>(false);
  const [repostCount, setRepostCount] = useState<number>(0);
  const [isBookmarked, setIsBookmarked] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const [quoteTarget, setQuoteTarget] = useState<QuoteTarget | null>(null);

  const openQuoteComposer = () => {
    setQuoteTarget({
      id: post.id,
      content: post.content,
      author: post.profiles?.display_name || post.profiles?.username || 'Unknown',
      avatarUrl: post.profiles?.avatar_url || null,
    });
  };

  const {
    pressing: longPressing,
    start: startLongPress,
    end: endLongPress,
    cancel: cancelLongPress,
    move: handleTouchMove,
  } = useLongPress({
    onLongPress: openQuoteComposer,
    onShortPress: () => void handleRepost(),
  });

  useEffect(() => {
    void loadPost();
  }, [id]);

  const loadPost = async () => {
    if (!id) return;
    setLoading(true);

    const { data } = await postService.getPostById(id);
    if (data) {
      setPost(data);
      setIsLiked(!!data.likes?.some((l: any) => l.user_id === user?.id));
      setLikeCount(data.likes?.length || 0);
      setIsReposted(!!data.reposts?.some((r: any) => r.user_id === user?.id));
      setRepostCount(data.reposts?.length || 0);
      setIsBookmarked(!!data.bookmarks?.some((b: any) => b.user_id === user?.id));

      // Replies live in the same table. Load the whole thread so nested
      // replies can be rendered under their parent.
      const rootId = data.root_post_id || data.id;
      const { data: replyData } = await postService.getReplies(rootId);
      setReplies(replyData || []);
    } else {
      setPost(null);
      setReplies([]);
    }

    setLoading(false);
  };

  const handleLike = async () => {
    if (!post) return;
    const prevLiked = isLiked;
    const prevCount = likeCount;
    setIsLiked(!prevLiked);
    setLikeCount((c) => (prevLiked ? Math.max(0, c - 1) : c + 1));

    try {
      const { error } = prevLiked
        ? await postService.unlikePost(id!)
        : await postService.likePost(id!);
      if (error) throw error;
    } catch (err: any) {
      setIsLiked(prevLiked);
      setLikeCount(prevCount);
      showError(err?.message || 'Could not update like.');
    }
  };

  const handleRepost = async () => {
    if (!post) return;
    const prevReposted = isReposted;
    const prevCount = repostCount;
    setIsReposted(!prevReposted);
    setRepostCount((c) => (prevReposted ? Math.max(0, c - 1) : c + 1));

    try {
      const { error } = prevReposted
        ? await postService.unrepostPost(id!)
        : await postService.repostPost(id!);
      if (error) throw error;
    } catch (err: any) {
      setIsReposted(prevReposted);
      setRepostCount(prevCount);
      showError(err?.message || 'Could not update repost.');
    }
  };

  const handleBookmark = async () => {
    if (!post) return;
    const prevBookmarked = isBookmarked;
    setIsBookmarked(!prevBookmarked);

    try {
      const { error } = prevBookmarked
        ? await postService.unbookmarkPost(id!)
        : await postService.bookmarkPost(id!);
      if (error) throw error;
    } catch (err: any) {
      setIsBookmarked(prevBookmarked);
      showError(err?.message || 'Could not update bookmark.');
    }
  };

  const handleReplySubmit = async (e: React.FormEvent, parentId: string) => {
    e.preventDefault();
    if (!post) return;
    if (!user) {
      showError('Please sign in to reply.');
      return;
    }
    if (!replyContent.trim()) return;

    const rootId = post.root_post_id || post.id;
    const { error } = await postService.createPost(replyContent.trim(), [], {
      replyToId: parentId,
      rootPostId: rootId,
    });
    if (error) {
      showError(error.message || 'Could not post reply.');
      return;
    }

    setReplyContent('');
    setReplyingToId(null);
    void loadPost();
  };

  const handleDeleteReply = async (replyId: string) => {
    if (!window.confirm('Delete this reply?')) return;
    const { error } = await postService.deletePost(replyId);
    if (error) {
      showError(error.message || 'Could not delete reply.');
      return;
    }
    toast('Reply deleted');
    void loadPost();
  };

  const handleQuote = (reply: any) => {
    setQuoteTarget({
      id: reply.id,
      content: reply.content,
      author: reply.profiles?.display_name || reply.profiles?.username || 'Unknown',
      avatarUrl: reply.profiles?.avatar_url || null,
    });
  };

  const closeError = () => setError(null);
  const showError = (message: string) => {
    setError(message);
    toast('Failed', message, 'destructive');
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="skeleton-text h-4 w-32" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-neutral-500 dark:text-neutral-400">Post not found</p>
      </div>
    );
  }

  const childrenMap = new Map<string, any[]>();
  replies.forEach((reply: any) => {
    if (!reply.reply_to_id) return;
    const list = childrenMap.get(reply.reply_to_id) || [];
    list.push(reply);
    childrenMap.set(reply.reply_to_id, list);
  });
  const topLevelReplies = childrenMap.get(post.id) || [];
  const replyTotal = post.comments_count ?? topLevelReplies.length;

  return (
    <div className="mx-auto max-w-2xl px-4 pt-4">
      <div
        className={cn(
          // Mobile: bottom border only, padding inside, no corner radius, no background block, full bleed.
          'border-b border-neutral-200 dark:border-neutral-800 p-3',
          // Desktop: full post card with padding, rounded corners, full border, no background block.
          'sm:post-card sm:p-4 sm:bg-transparent sm:dark:bg-transparent',
        )}
      >
        <div className="flex gap-3">
          <UserAvatar src={post.profiles?.avatar_url} size="md" />
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="font-semibold">
                {post.profiles?.display_name || post.profiles?.username}
              </span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400">
                @{post.profiles?.username}
              </span>
              <span className="text-xs text-neutral-400 dark:text-neutral-500">
                · {formatRelativeDate(post.created_at)}
              </span>
            </div>
            <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
              {post.content}
            </div>
            {post.quoted_post && <QuotedContent post={post.quoted_post} />}
            {post.post_media && post.post_media.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {post.post_media.map((m: any, i: number) => (
                  <img
                    key={i}
                    src={m.url}
                    alt="Media"
                    className="aspect-square w-full rounded-xl object-cover"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-4 text-sm">
          <button
            onClick={handleLike}
            className={cn(
              'flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
              isLiked
                ? 'text-brand-600 dark:text-brand-400'
                : 'text-neutral-600 dark:text-neutral-300',
            )}
            aria-label={isLiked ? 'Unlike' : 'Like'}
          >
            <Heart size={18} className={cn(isLiked && 'fill-current')} />
            <span>{likeCount}</span>
          </button>
          <button
            onClick={() => document.getElementById('reply-input')?.focus()}
            className="flex items-center gap-1 rounded-lg px-2 py-1.5 text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
            aria-label="Replies"
          >
            <MessageCircle size={18} />
            <span>{replyTotal}</span>
          </button>
          <button
            onClick={handleRepost}
            className={cn(
              'flex items-center gap-1 rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
              isReposted
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-neutral-600 dark:text-neutral-300',
            )}
            aria-label={isReposted ? 'Undo repost' : 'Repost'}
          >
            <Repeat size={18} />
            <span>{repostCount}</span>
          </button>
          <button
            onClick={handleBookmark}
            className={cn(
              'flex items-center rounded-lg px-2 py-1.5 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800',
              isBookmarked
                ? 'text-brand-600 dark:text-brand-400'
                : 'text-neutral-600 dark:text-neutral-300',
            )}
            aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark'}
          >
            <Bookmark size={18} className={cn(isBookmarked && 'fill-current')} />
          </button>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="mb-3 text-lg font-semibold">Replies</h3>

        <form onSubmit={(e) => handleReplySubmit(e, post.id)} className="flex items-center gap-3 border-b border-neutral-200 pb-3 dark:border-neutral-800">
          <UserAvatar size="md" className="shrink-0" />
          <input
            id="reply-input"
            type="text"
            value={replyingToId && replyingToId !== post.id ? '' : replyContent}
            onChange={(e) => {
              setReplyingToId(post.id);
              setReplyContent(e.target.value);
            }}
            placeholder={!user ? 'Sign in to reply' : 'Post your reply...'}
            disabled={!user}
            className="min-w-0 flex-1 bg-transparent text-sm focus:ring-0 placeholder:text-neutral-400 dark:placeholder:text-neutral-500"
          />
          <button
            type="submit"
            disabled={!replyContent.trim() || !user}
            className="shrink-0 text-sm font-medium text-brand-600 disabled:opacity-50 dark:text-brand-400"
          >
            Reply
          </button>
        </form>

        {topLevelReplies.length === 0 && (
          <p className="mt-3 text-sm text-neutral-500 dark:text-neutral-400">
            No replies yet. Be the first to reply!
          </p>
        )}

        {topLevelReplies.map((reply: any) => (
          <ReplyCard
            key={reply.id}
            reply={reply}
            depth={0}
            childrenMap={childrenMap}
            replyContent={replyContent}
            setReplyContent={setReplyContent}
            replyingToId={replyingToId}
            onStartReply={(replyId) => {
              setReplyingToId(replyId);
              setReplyContent('');
            }}
            onSubmitReply={handleReplySubmit}
            onQuote={handleQuote}
            onDelete={handleDeleteReply}
          />
        ))}
      </div>

      {error && (
        <Modal isOpen={!!error} onClose={closeError}>
          <h2 className="mb-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Something went wrong
          </h2>
          <p className="mb-6 text-neutral-600 dark:text-neutral-400">{error}</p>
          <button
            onClick={closeError}
            className="inline-flex items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            <X size={16} className="mr-2" />
            Dismiss
          </button>
        </Modal>
      )}

      <QuoteModal
        target={quoteTarget}
        onClose={() => setQuoteTarget(null)}
        onQuoted={() => toast('Quoted', 'Quote shared on your profile.')}
      />
    </div>
  );
}
